import { ProjectMetadata } from '@shared/models/project.model';
import { ActivityId } from '@shared/models/activity-class.model';
import {
  Activity,
  ActivityEnum,
  ActivityMix,
} from '@shared/models/activities.model';
import { createActivity } from '../activities.helpers';
import {
  createActivityFromActivityId,
  migrateStringGfa,
} from '../design/migration/legacy-create-activity-from-id.helpers';
import { getBuildingGFA } from '../expression-variables.helpers';
import { sum } from '../math.helpers';
import { HourNumbers, WeekdayNumbers, WeekNumbers } from 'luxon';
import { SemiPartial } from '@shared/models/type-helpers.model';
import {
  OFFICE_DEFAULT_SCHEDULES,
  RESIDENTIAL_DEFAULT_SCHEDULES,
  SCHEDULES_OTHER,
  SCHOOL_DEFAULT_SCHEDULES,
} from './activity-schedules.constants';
import { getHeatedArea } from './energy.helpers';
import { toEnergyActivity } from './to-energy-activity.helpers';
import { BuildingDesign } from '@shared/models/design/building-design.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { getActivityMixFromDesign } from '../design/activity-mix.helpers';
import { getAllStoreys } from '../design/storey.helpers';
import { guards } from '../typeguards.helpers';

type LegacyActivityMetadata = ProjectMetadata & {
  activity_id?: ActivityId;
  activities?: Activity[];
};

/**
 * @deprecated Use Activity from geometry/activity.model instead
 */
export type SpaceActivity = {
  /**
   * Which type of activity this is. Note that some types that can't be selected by the user is allowed here for testing purposes.
   */
  type: ActivityType;

  /**
   * Area in m²
   */
  area: number;

  /**
   * The schedules of the activity
   */
  schedules: ActivitySchedule[];

  /**
   * The properties of the activity
   */
  properties: ActivityProperties;
};

type ActivityProperties = {
  ventFlowPerM2InM3PerS?: number;
  postHeatingTemp?: number;
  preCoolingTemp?: number;
  occupantCountPerM2?: number;

  /**
   * If there are 100 residents in the building and at a certain hour 60 people are present,
   * then the occupantMultiplier for that hour could be 0.6.
   * typically a value between 0 and 1 where 1 is 100% of the occupants are present
   */
  occupantMultiplier?: number;
  activityMultiplier?: number;
  heatPowerCap?: number;
  coolPowerCap?: number;
  Htr_WperK_m2?: number;

  /** Which temperature the space should be heated to (°C) */
  heatSet?: number;
  /** Which temperature the space should be cooled to (°C) */
  coolSet?: number;
  deadbandK?: number;
  heatPowerLimitWPerM2?: number;
  /** How much cooling can be provided (w/m2) */
  coolPowerLimitWPerM2?: number;
};

export type ActivitySchedule = {
  /** When the activity is active */
  operation: {
    hours?: (HourNumbers | [HourNumbers, HourNumbers])[];
    wdays?: number | (WeekdayNumbers | [WeekdayNumbers, WeekdayNumbers])[];
    weeks?: number | (WeekNumbers | [WeekNumbers, WeekNumbers])[];
  };
  properties: ActivityProperties;
};

/**
 * @deprecated use getActivityMixFromDesign instead. Get the activity mix from the project metadata. IE how much m2 of each activity type is in the building.
 * @param meta Project metadata.
 * @returns Activity mix.
 */
export const getActivityMix = (
  activityId: ActivityId | undefined,
  heatedArea: number,
): ActivityMix => {
  // TODO: Use activities to calculate the activity mix
  return {
    [toEnergyActivity(activityId)]: heatedArea,
  };
};

export {
  createActivityFromActivityId,
  migrateStringGfa,
} from '../design/migration/legacy-create-activity-from-id.helpers';

const DEFAULT_APARTMENTS_RATIO = 0.85;

/**
 * @deprecated Make sure we ave activitites in the building
 * @param meta Project metadata.
 * @returns
 */
export const fallbackActivites = (meta: LegacyActivityMetadata): Activity[] => {
  const { activities, activity_id } = meta;
  const gfa = getBuildingGFA(meta);
  // const energyActivity = toEnergyActivity(meta.activity_id);

  if (!activities?.length) {
    return [
      createActivityFromActivityId(activity_id),
      {
        ...createActivity(ActivityEnum.Other),
        activityId: ActivityId.CommonAreas,
        gfa: gfa * (1 - DEFAULT_APARTMENTS_RATIO),
      },
    ];
  }
  return activities;
};

type IFactorySpaceActivity = SemiPartial<SpaceActivity, 'type' | 'area'>;

/**
 * @deprecated Use new design.activities instead. TODO Remove fully when everything is migrated to design.activities
 * @param defaults
 * @returns
 */
export const createSpaceActivity = (
  defaults: IFactorySpaceActivity,
): SpaceActivity => {
  const schedules = getActivitySchedules(defaults);
  const properties = defaults.properties ?? {};

  return {
    ...defaults,
    schedules,
    properties,
  };
};

/**
 * Get the schedules for an activity.
 * @param activity - The activity to get the schedules for.
 * @returns The schedules for the activity.
 */
type ActivityScheduleInput = {
  type: ActivityType;
  schedules?: ActivitySchedule[];
};

export const getActivitySchedules = ({
  schedules,
  type,
}: ActivityScheduleInput): ActivitySchedule[] => {
  if (schedules?.length) {
    return schedules;
  }
  switch (type) {
    case ActivityType.Office:
      return OFFICE_DEFAULT_SCHEDULES;
    case ActivityType.School:
      return SCHOOL_DEFAULT_SCHEDULES;
    case ActivityType.Residential:
      return RESIDENTIAL_DEFAULT_SCHEDULES;
    default:
      return SCHEDULES_OTHER;
  }
};

/**
 * Get space activities from design if available, otherwise fall back to metadata
 * @param meta Project metadata
 * @param design Optional building design (geometry 2.0)
 * @param heatedArea Provide heated area to override the default area used for the activities (=gfa)
 * @returns Space activities array.
 */
export const getSpaceActivitiesFromMeta = (
  meta: ProjectMetadata,
  design?: BuildingDesign,
  heatedArea: number = getHeatedArea(meta),
): SpaceActivity[] => {
  // Use design.activities if available
  if (design?.activities?.length && getAllStoreys(design).length) {
    const activityAreas = getActivityMixFromDesign(design);

    // Create space activities from design activities
    return design.activities
      .map((activity): SpaceActivity | undefined => {
        const area = activityAreas[activity.type] ?? 0;
        if (area <= 0) return; // Skip activities with no area

        return createSpaceActivity({
          type: toEnergyActivity(activity.type),
          area,
        });
      })
      .filter(guards.is.defined);
  }

  // Fallback to old metadata-based approach
  const activitiesWithFallback = fallbackActivites(meta);
  const activities: Activity[] = activitiesWithFallback.map(migrateStringGfa);
  const activitiesWithoutGfa = activities.filter(
    (activity) =>
      'gfa' in activity === false || typeof activity.gfa !== 'number',
  ).length;
  const remainingArea = heatedArea - sum(activities, 'gfa');

  return activities.map((activity): SpaceActivity =>
    createSpaceActivity({
      type: toEnergyActivity(activity),
      area:
        'gfa' in activity && typeof activity.gfa === 'number'
          ? activity.gfa
          : remainingArea / (activitiesWithoutGfa || 1),
    }),
  );
};
